import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsServiceProxy, getErrorTrackingObject } from '@rci/self-serve-fido/core/analytics';
import { AuthenticationService } from '@rci/self-serve-fido/shared/authentication/authentication.service';
import { UniqueAlertId } from '@rci/self-serve-fido/shared/billing-alerts/state.interface';
import { AccountDetailService } from '@rci/self-serve-fido/state/account/account-detail.service';
import { BillingDetailService } from '@rci/self-serve-fido/state/billing-detail/billing-detail.service';
import { ApiEndpointService } from '@rci/self-serve-shared';
import { AnalyticsTrackingType } from '@rogers/self-serve-analytics';
import { AccountDetail } from '@rogers/self-serve-common/account';
import { AlertFacadeService } from '@rogers/self-serve-common/alert';
import { fetchAlertContent } from '@rogers/self-serve-common/alert-content';
import { BillingDetail } from '@rogers/self-serve-common/billing-detail';
import { PromiseToPayFacadeService } from '@rogers/self-serve-promise-to-pay';
import {
  PromiseToPay,
  PtpAndReportDetails,
  PtpContentDetails,
  PtpDetail,
  PtpInstallments,
} from '@rogers/self-serve-promise-to-pay/promise-to-pay.model';
import { BehaviorSubject, EMPTY, Observable, combineLatest, of, throwError } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PromiseToPayService {
  isPtpDetailLoaded$ = new BehaviorSubject<boolean>(false);
  private _getPtpDetails: PtpContentDetails[] = [];
  private _ptpInstallments: Array<PtpInstallments>;
  private _selectedAccountPtpDetail: PtpDetail;

  constructor(
    private readonly accountDetailService: AccountDetailService,
    private readonly alertFacadeService: AlertFacadeService,
    private readonly analyticsServiceProxy: AnalyticsServiceProxy,
    private readonly apiEndpointService: ApiEndpointService,
    private readonly authenticationService: AuthenticationService,
    private readonly billingDetailService: BillingDetailService,
    private readonly http: HttpClient,
    private readonly promiseToPayFacadeService: PromiseToPayFacadeService,
    private readonly store: Store,
    private readonly translateService: TranslateService,
  ) {}

  get getPtpDetails() {
    return this._getPtpDetails;
  }

  set getPtpDetails(value) {
    this._getPtpDetails = value;
  }

  get isPrepaidAccount$() {
    return this.accountDetailService.getSelectedAccountDetail$().pipe(map(account => account.isPrepaidAccount));
  }

  get notEligibleMessageConfig() {
    return {
      alertIcon: 'warning-filled',
      alertLinkAlt: 'promise-to-pay.contact-us-alt',
      alertLinkCopy: 'promise-to-pay.contact-us',
      alertLinkUrl: '/consumer/contact-us',
      alertVariant: 'warning',
      body: 'promise-to-pay.not-eligible.body',
      heading: 'promise-to-pay.not-eligible.header',
      iconCaption: 'Attention',
      isSuccess: false,
    };
  }

  get ptpInstallments() {
    return this._ptpInstallments;
  }

  set ptpInstallments(value) {
    this._ptpInstallments = value;
  }

  get selectedAccountPtpDetail() {
    return this._selectedAccountPtpDetail;
  }

  set selectedAccountPtpDetail(value) {
    this._selectedAccountPtpDetail = value;
  }

  private get createPTPSuccessConfig() {
    return {
      alertIcon: 'checkmark-outlined',
      alertVariant: 'success',
      body: 'promise-to-pay.success-ptp.content',
      buttonAlt: 'promise-to-pay.setup-ptp.done-btn-alt',
      buttonLabel: 'promise-to-pay.success-ptp.done-btn',
      heading: 'promise-to-pay.success-ptp.header',
      iconCaption: '',
      isSuccess: true,
    };
  }

  filterPtpDetails(accountNumber: string) {
    const ptpDetails = this.getPtpDetails.filter(item => item.accountNumber !== accountNumber);
    this.getPtpDetails = ptpDetails;
  }

  createPTP$(ptpRequest) {
    return this.accountDetailService.getSelectedAccountDetail$().pipe(
      filter(accountDetail => !!accountDetail),
      switchMap(accountDetail => {
        const createPTPURL = `${this.apiEndpointService.paymentBaseUrl}${accountDetail.alias}/payments/promise-to-pay`;
        return this.http
          .post<any>(createPTPURL, ptpRequest, {
            headers: this.authenticationService.getDomainServiceHeaders(),
          })
          .pipe(
            map(() => {
              this.filterPtpDetails(accountDetail.accountNumber);
              this.alertFacadeService.dismissAlert(
                `${accountDetail.accountNumber}_${UniqueAlertId.PROMISE_TO_PAY_SETUP}`,
              );
              return { config: this.createPTPSuccessConfig };
            }),
            catchError(err => this.handleCreatePTPError(err)),
          );
      }),
    );
  }

  getPTP$(accountAlias: string, accountNumber: string): Observable<PromiseToPay> {
    return this.promiseToPayFacadeService
      .loadPromiseToPay$(accountNumber, accountAlias)
      .pipe(switchMap(() => this.promiseToPayFacadeService.getPromiseToPay$(accountNumber)));
  }

  getPtpAndReportDetails$(accountDetail: AccountDetail, billingDetail: BillingDetail): Observable<PtpAndReportDetails> {
    const { accountNumber, alias } = accountDetail;
    const { displayActiveIpp, pastDueBalance } = billingDetail;

    return this.getPTP$(alias, accountNumber).pipe(
      map(ptpResponse =>
        this.transformPtpAndReportDetails(accountNumber, displayActiveIpp, pastDueBalance, ptpResponse),
      ),
      catchError(() => of({ accountNumber, showPtpAlert: false })),
    );
  }

  loadPromiseToPay(accountAlias: string, accountNumber: string, refresh = false) {
    return this.promiseToPayFacadeService.loadPromiseToPay$(accountNumber, accountAlias, refresh).subscribe();
  }

  loadPtpAlertContent() {
    setTimeout(() =>
      this.store.dispatch(
        fetchAlertContent({ language: this.translateService.currentLang, pageName: 'billing-alerts' }),
      ),
    );
  }

  loadSelectedAccountPtpDetails$() {
    this.isPtpDetailLoaded$.next(false);

    return combineLatest([
      this.accountDetailService.getSelectedAccountDetail$(),
      this.billingDetailService.getBillingDetail$(),
    ]).pipe(
      take(1),
      switchMap(([accountDetail, billingDetail]) => {
        this.selectedAccountPtpDetail = {
          accountNumber: accountDetail.accountNumber,
          currentDate: null,
          eligible: false,
        };

        if (
          !!accountDetail &&
          !accountDetail.isCancelledAccount &&
          !accountDetail.isTentative &&
          accountDetail.isWirelessOrConsolidatedAccount &&
          !!billingDetail &&
          !billingDetail.hasError &&
          !billingDetail.displayActiveIpp &&
          billingDetail.pastDueBalance > 0
        ) {
          return this.getPTP$(accountDetail.alias, accountDetail.accountNumber).pipe(
            take(1),
            filter(ptpResponse => ptpResponse.createEligibility),
            tap(ptpResponse => {
              this.isPtpDetailLoaded$.next(true);
              this.selectedAccountPtpDetail = {
                accountAlias: accountDetail.alias,
                accountNumber: accountDetail.accountNumber,
                currentDate: new Date(),
                eligible: true,
                isPtpNType: ptpResponse.ptpNType,
                pastDueBalance: billingDetail.pastDueBalance,
                recommendedAmount: ptpResponse.recommendedAmount,
              };
            }),
          );
        }
        return EMPTY;
      }),
      finalize(() => this.isPtpDetailLoaded$.next(true)),
    );
  }

  trackGetPTPError(error) {
    const errorPayload = {
      ...getErrorTrackingObject(error),
      API: error.url.replace(/accounts\/(.+)\/payments\/promise-to-pay/, 'accounts/payments/promise-to-pay'),
      type: (error.status === 401 ? 'user' : 'http') as AnalyticsTrackingType,
    };
    this.analyticsServiceProxy.trackError(errorPayload, error.message);
    return throwError(error);
  }

  private handleCreatePTPError(error) {
    const config = {
      alertIcon: 'error-outlined',
      alertLinkAlt: 'promise-to-pay.contact-us-alt',
      alertLinkCopy: 'promise-to-pay.contact-us',
      alertLinkUrl: '/consumer/contact-us',
      alertVariant: 'error',
      body: 'promise-to-pay.error-ptp.system-error-content',
      buttonAlt: 'promise-to-pay.error-ptp.back-btn-alt',
      buttonLabel: 'promise-to-pay.error-ptp.back-btn',
      heading: 'promise-to-pay.error-ptp.system-error-header',
      iconCaption: 'Attention',
      isSuccess: false,
    };

    if (error.status === 422) {
      config.alertLinkUrl = null;
      config.body = 'promise-to-pay.error-ptp.not-eligible-content';
      config.heading = 'promise-to-pay.error-ptp.not-eligible-header';
    }
    return of({ config, error });
  }

  private transformPtpAndReportDetails(
    accountNumber,
    displayActiveIpp,
    pastDueBalance,
    ptpResponse,
  ): PtpAndReportDetails {
    if (
      !!ptpResponse &&
      ptpResponse.reportPayment &&
      !ptpResponse.createEligibility &&
      ptpResponse.installments?.length
    ) {
      const reportedAmount = ptpResponse.installments.reduce((total, item) => {
        return total + item.amount;
      }, 0);
      const reportedDate = ptpResponse.finalInstallmentDate;
      return { accountNumber, reportedAmount, reportedDate, showPtpAlert: false, showReportPaymentAlert: true };
    }

    if (displayActiveIpp || pastDueBalance <= 0) {
      return { accountNumber, showPtpAlert: false };
    }

    if (ptpResponse.createEligibility) {
      return {
        accountNumber,
        setupPtp: true,
        showPtpAlert: true,
      };
    }

    if (!!ptpResponse && !ptpResponse.installments) {
      return { accountNumber, showPtpAlert: false };
    }

    const pendingInstallments = ptpResponse.installments.filter(
      installment => installment.status.toLowerCase() === 'pending',
    );
    const pendingBalance = pendingInstallments.reduce((sum, installment) => sum + installment.amount, 0);
    this.ptpInstallments = ptpResponse.installments;
    return {
      accountNumber,
      isSinglePtp: pendingInstallments.length === 1,
      pastDueBalance: pendingBalance,
      ptpDueDate: ptpResponse.finalInstallmentDate,
      setupPtp: false,
      showPtpAlert: pendingBalance > 0,
    };
  }
}
